// The piece's sound layer. Everything is synthesized with WebAudio — no
// audio assets, and definitely nothing of Microsoft's. Sparse on purpose:
// clicks, a gentle CRT hum, soft card sounds. No startup chime.
//
// The AudioContext must be created/resumed inside a user gesture; the
// login dialog's OK click calls unlock(). Every play function is a no-op
// until then, so callers never need to check.

import { readSetting, writeSetting } from "../hooks/useSetting";

// Audio graph handles live on globalThis so a dev-server hot reload of
// this module reuses (and can silence) the existing context instead of
// stacking a second hum on top of an orphaned first one. Harmless in
// production (one module instance, one stash).
const STASH = (globalThis.__saatSound = globalThis.__saatSound || {
  ctx: null,
  master: null,
  hum: null,
});
let ctx = STASH.ctx;
let master = STASH.master;
if (STASH.hum) {
  // A previous module instance left a hum running; take it over.
  try {
    STASH.hum.stop();
  } catch {
    // already stopped
  }
  STASH.hum = null;
}

// Master volume (0..1), persisted through the ONE settings store
// (hooks/useSetting); writeSetting also fires the event every volume
// UI listens for.
const VOLUME_KEY = "saat.volume";
let volume = (() => {
  const v = readSetting(VOLUME_KEY, 0.5);
  return Number.isFinite(v) ? Math.min(1, Math.max(0, v)) : 0.5;
})();

export function setVolume(v) {
  volume = Math.min(1, Math.max(0, v));
  if (master) master.gain.value = volume;
  writeSetting(VOLUME_KEY, volume);
}

export function getVolume() {
  return volume;
}

export function unlock() {
  if (dead) return; // powered off: the speaker is gone for good
  if (ctx) {
    syncSuspend();
    return;
  }
  const AC = window.AudioContext || window.webkitAudioContext;
  if (!AC) return;
  ctx = new AC();
  master = ctx.createGain();
  master.gain.value = volume;
  master.connect(ctx.destination);
  STASH.ctx = ctx;
  STASH.master = master;
  syncSuspend();
}

export function isUnlocked() {
  return ctx != null;
}

// The tab being hidden and the screensaver running each suspend the
// whole context (hum included) independently; audio resumes only when
// the LAST reason clears, so waking one in the background can't bring
// the hum back early. Suspending beats muting: no battery spent
// synthesizing sound nobody can hear.
const pauseReasons = new Set();
if (typeof document !== "undefined" && document.hidden) {
  pauseReasons.add("hidden");
}

function syncSuspend() {
  if (!ctx) return;
  if (pauseReasons.size > 0) ctx.suspend();
  else ctx.resume();
}

// reason: "hidden" | "screensaver".
export function setPauseReason(reason, active) {
  if (active) pauseReasons.add(reason);
  else pauseReasons.delete(reason);
  syncSuspend();
}

if (typeof document !== "undefined") {
  document.addEventListener("visibilitychange", () => {
    setPauseReason("hidden", document.hidden);
  });
}

// A dry mechanical mouse click: two tiny filtered noise ticks (down+up).
export function click() {
  if (!ctx) return;
  const t0 = ctx.currentTime;
  for (const [at, gain, freq] of [
    [0, 0.6, 2400],
    [0.055, 0.35, 1900],
  ]) {
    const dur = 0.012;
    const buf = ctx.createBuffer(1, ctx.sampleRate * dur, ctx.sampleRate);
    const data = buf.getChannelData(0);
    for (let i = 0; i < data.length; i++) {
      data[i] = (Math.random() * 2 - 1) * (1 - i / data.length);
    }
    const src = ctx.createBufferSource();
    src.buffer = buf;
    const bp = ctx.createBiquadFilter();
    bp.type = "bandpass";
    bp.frequency.value = freq;
    bp.Q.value = 1.2;
    const g = ctx.createGain();
    g.gain.value = gain;
    src.connect(bp).connect(g).connect(master);
    src.start(t0 + at);
  }
}

// Soft card-on-felt: a short lowpassed noise thump with a fast decay.
// `gainScale` lets neighbor windows play their cards faintly.
export function cardPlace(gainScale = 1) {
  if (!ctx) return;
  const t0 = ctx.currentTime;
  const dur = 0.05;
  const buf = ctx.createBuffer(1, ctx.sampleRate * dur, ctx.sampleRate);
  const data = buf.getChannelData(0);
  for (let i = 0; i < data.length; i++) {
    data[i] = (Math.random() * 2 - 1) * Math.pow(1 - i / data.length, 2);
  }
  const src = ctx.createBufferSource();
  src.buffer = buf;
  const lp = ctx.createBiquadFilter();
  lp.type = "lowpass";
  lp.frequency.value = 900 + Math.random() * 300;
  const g = ctx.createGain();
  g.gain.value = 0.5 * gainScale;
  src.connect(lp).connect(g).connect(master);
  src.start(t0);
}

// A card sliding/flipping: brighter, slightly longer noise swish.
export function cardFlip(gainScale = 1) {
  if (!ctx) return;
  const t0 = ctx.currentTime;
  const dur = 0.09;
  const buf = ctx.createBuffer(1, ctx.sampleRate * dur, ctx.sampleRate);
  const data = buf.getChannelData(0);
  for (let i = 0; i < data.length; i++) {
    const env = Math.sin((i / data.length) * Math.PI);
    data[i] = (Math.random() * 2 - 1) * env * env;
  }
  const src = ctx.createBufferSource();
  src.buffer = buf;
  const bp = ctx.createBiquadFilter();
  bp.type = "bandpass";
  bp.frequency.value = 2600;
  bp.Q.value = 0.7;
  const g = ctx.createGain();
  g.gain.value = 0.22 * gainScale;
  src.connect(bp).connect(g).connect(master);
  src.start(t0);
}

// The little confirmation ding the tray volume slider plays when you
// release it, so you hear the level you just set. A short soft bell —
// sine + a quieter second harmonic — synthesized, not Microsoft's
// Ding.wav. Runs through master, so it's automatically at the new
// volume (and silent when muted, which is honest).
export function volumeDing() {
  if (!ctx) return;
  const t0 = ctx.currentTime;
  const g = ctx.createGain();
  g.gain.setValueAtTime(0.0001, t0);
  g.gain.exponentialRampToValueAtTime(0.16, t0 + 0.01);
  g.gain.exponentialRampToValueAtTime(0.0001, t0 + 0.32);
  g.connect(master);
  for (const [freq, gain] of [
    [740, 1],
    [1480, 0.25],
  ]) {
    const o = ctx.createOscillator();
    o.type = "sine";
    o.frequency.value = freq;
    const og = ctx.createGain();
    og.gain.value = gain;
    o.connect(og).connect(g);
    o.start(t0);
    o.stop(t0 + 0.35);
  }
}

// The "you clicked outside a modal dialog" chord — Win95's Default Beep
// gesture, synthesized (never Microsoft's Ding.wav). Slightly blunter
// than volumeDing: lower, quicker, a polite "no". Plays alongside the
// dialog's title-bar flash.
export function modalDing() {
  if (!ctx) return;
  const t0 = ctx.currentTime;
  const g = ctx.createGain();
  g.gain.setValueAtTime(0.0001, t0);
  g.gain.exponentialRampToValueAtTime(0.14, t0 + 0.008);
  g.gain.exponentialRampToValueAtTime(0.0001, t0 + 0.22);
  g.connect(master);
  for (const [freq, gain] of [
    [523, 1],
    [1046, 0.18],
  ]) {
    const o = ctx.createOscillator();
    o.type = "triangle";
    o.frequency.value = freq;
    const og = ctx.createGain();
    og.gain.value = gain;
    o.connect(og).connect(g);
    o.start(t0);
    o.stop(t0 + 0.25);
  }
}

// The CRT hum: a very quiet 60Hz-ish tone + its second harmonic + a
// whisper of filtered noise, faded in over a couple seconds. Runs until
// stopHum. Deliberately at the edge of perception.
let hum = null;

export function startHum() {
  if (!ctx || hum) return;
  const g = ctx.createGain();
  g.gain.value = 0;
  g.gain.linearRampToValueAtTime(0.01, ctx.currentTime + 2.5);
  g.connect(master);

  const o1 = ctx.createOscillator();
  o1.type = "sine";
  o1.frequency.value = 60;
  const o2 = ctx.createOscillator();
  o2.type = "sine";
  o2.frequency.value = 120;
  const o2g = ctx.createGain();
  o2g.gain.value = 0.4;

  const noiseDur = 2;
  const buf = ctx.createBuffer(1, ctx.sampleRate * noiseDur, ctx.sampleRate);
  const data = buf.getChannelData(0);
  for (let i = 0; i < data.length; i++) data[i] = Math.random() * 2 - 1;
  const noise = ctx.createBufferSource();
  noise.buffer = buf;
  noise.loop = true;
  const nf = ctx.createBiquadFilter();
  nf.type = "highpass";
  nf.frequency.value = 8000;
  const ng = ctx.createGain();
  // The "fuzz" noise floor — kept low, felt more than heard under the 60Hz tone.
  ng.gain.value = 0.04;

  o1.connect(g);
  o2.connect(o2g).connect(g);
  noise.connect(nf).connect(ng).connect(g);
  o1.start();
  o2.start();
  noise.start();
  hum = { g, stop: () => [o1, o2, noise].forEach((n) => n.stop()) };
  STASH.hum = hum;
}

export function stopHum() {
  if (!ctx || !hum) return;
  const h = hum;
  hum = null;
  STASH.hum = null;
  h.g.gain.linearRampToValueAtTime(0, ctx.currentTime + 0.5);
  setTimeout(() => h.stop(), 600);
}

// The machine is off. Hum fades, then the whole AudioContext closes
// and stays closed — no ambient card sounds, clicks, or dings can
// play over "It's now safe to close this tab", and a later gesture
// can't resurrect audio (unlock() refuses once dead).
let dead = false;
export function powerOff() {
  dead = true;
  stopHum();
  const c = ctx;
  ctx = null;
  STASH.ctx = null;
  master = null;
  if (c) {
    // Let stopHum's 0.5s fade finish on the closing context.
    setTimeout(() => {
      try {
        c.close();
      } catch {
        // Already closed: fine, it's off either way.
      }
    }, 700);
  }
}
